import { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Bot, 
  Send, 
  Sparkles, 
  RotateCcw, 
  FileText, 
  ShieldCheck, 
  AlertCircle, 
  CheckCircle2, 
  ChevronRight,
  HelpCircle,
  ExternalLink
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/Sheet.jsx';
import { Button } from '../ui/Button.jsx';
import { BomCard } from './BomCard.jsx';
import { CopilotMessageRenderer } from './CopilotMessageRenderer.jsx';
import { useOptionalCart } from '../../store/cart.jsx';
import { cn } from '../../lib/utils.js';

const DEFAULT_SUGGESTIONS = [
  'Passendes Netzteil für MB-Secure?',
  'Drahtloser Bewegungsmelder VdS Klasse C',
  'Stückliste für 4-Zonen Brandmeldeanlage',
  'IP-Kamera mit Kennzeichenerkennung',
];

export function createEphemeralSessionId({
  cryptoImpl = globalThis.crypto,
  now = Date.now,
  random = Math.random,
} = {}) {
  if (cryptoImpl && typeof cryptoImpl.randomUUID === 'function') {
    return cryptoImpl.randomUUID();
  }
  return `sess_${now()}_${random().toString(36).slice(2, 9)}`;
}

export function buildCopilotChatRequestBody({
  messages = [],
  userMessage,
  tenantId,
  sessionId,
} = {}) {
  const history = Array.isArray(messages) ? messages : [];
  const nextMessages = userMessage ? [...history, userMessage] : history;
  return {
    messages: nextMessages.map((message) => ({
      role: message.role,
      content: message.content,
    })),
    tenantId,
    sessionId,
  };
}

/**
 * Extracts inline citation markers from text, e.g. [Indigo Melder S. 12] or [Quelle: MB-Secure Handbuch, S. 45]
 */
export function parseMessageWithCitations(text = '', onCitationClick) {
  if (!text || typeof text !== 'string') return text;

  // Match citation patterns like [Doc Name S. 12] or [Quelle: ...] or [VdS 2311]
  const citationRegex = /\[(Quelle:\s*[^\]]+|[^\]]+?\s+S\.\s*\d+|VdS\s*[\d]+|DIN\s*EN\s*[\d-]+)\]/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let matchCount = 0;

  while ((match = citationRegex.exec(text)) !== null) {
    matchCount++;
    const start = match.index;
    const end = citationRegex.lastIndex;

    if (start > lastIndex) {
      parts.push(text.substring(lastIndex, start));
    }

    const citationText = match[1];
    parts.push(
      <button
        key={`cit-${start}`}
        type="button"
        data-testid="citation-pill"
        data-citation-ref={citationText}
        onClick={() => onCitationClick && onCitationClick(citationText)}
        className="inline-flex items-center gap-1 mx-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[var(--surface-container-high,#f1f5f9)] border border-[var(--border)] text-[var(--foreground)] hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors cursor-pointer shadow-2xs align-baseline"
        title={`Nachweis anzeigen: ${citationText}`}
      >
        <FileText className="h-3 w-3 text-[var(--primary)] shrink-0" />
        <span className="truncate max-w-[200px]">{citationText}</span>
      </button>
    );

    lastIndex = end;
  }

  if (matchCount === 0) return text;

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts;
}

export function CopilotDrawerFlyout({
  open,
  onOpenChange,
  tenant = {},
  initialMessages,
  onImportBom,
  onNavigate,
  className = '',
}) {
  const optionalCart = useOptionalCart();
  const cartDispatch = optionalCart?.dispatch;

  const tenantTitle = tenant?.settings?.copilot_title || `${tenant?.name || 'ASL'} Fachassistent`;
  const defaultWelcome = `Willkommen! Ich bin Ihr technischer Assistent für Sicherheitstechnik. Wie kann ich Sie bei Normen (VdS/DIN), Datenblättern oder Stücklisten unterstützen?`;

  const [messages, setMessages] = useState(() => {
    if (initialMessages && Array.isArray(initialMessages) && initialMessages.length > 0) {
      return initialMessages;
    }
    return [
      {
        id: 'msg-welcome',
        role: 'assistant',
        content: defaultWelcome,
        timestamp: new Date().toISOString(),
      },
    ];
  });

  const [sessionId] = useState(createEphemeralSessionId);

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeCitation, setActiveCitation] = useState(null);
  const [bannerNotice, setBannerNotice] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => inputRef.current?.focus(), 150);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleResetChat = useCallback(() => {
    setMessages([
      {
        id: `msg-welcome-${Date.now()}`,
        role: 'assistant',
        content: defaultWelcome,
        timestamp: new Date().toISOString(),
      },
    ]);
    setActiveCitation(null);
    setBannerNotice(null);
  }, [defaultWelcome]);

  const handleImportBomInternal = useCallback(async (bomOrItems, idempotencyKeyPassed) => {
    const rawItems = Array.isArray(bomOrItems)
      ? bomOrItems
      : Array.isArray(bomOrItems?.items)
        ? bomOrItems.items
        : [];

    if (rawItems.length === 0) return;

    const idempotencyKey =
      idempotencyKeyPassed ||
      (typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `bom_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);

    const activeTenantId = tenant?.id || '';

    try {
      // 1. Invoke POST /api/cart/bom-import with current tenant headers
      const response = await fetch('/api/cart/bom-import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': activeTenantId,
          'X-Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify({
          tenantId: activeTenantId,
          idempotency_key: idempotencyKey,
          items: rawItems.map((it) => ({
            sku: it.sku || it.productId || it.product_id || it.id,
            productId: it.productId || it.product_id || it.id,
            quantity: Math.max(1, parseInt(it.quantity, 10) || 1),
            role: it.role,
            name: it.name,
            norm_citation: it.norm_citation || it.normCitation || it.norm_justification,
          })),
        }),
      });

      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.error || errJson.message || `BoM Import fehlgeschlagen (HTTP ${response.status})`);
      }

      const verifiedData = await response.json();
      const verifiedItems = verifiedData.items || rawItems;

      // 2. Dispatch IMPORT_VERIFIED_BOM to cart store if cart dispatch is available
      if (cartDispatch) {
        cartDispatch({
          type: 'IMPORT_VERIFIED_BOM',
          items: verifiedItems,
          idempotencyKey: verifiedData.idempotency_key || idempotencyKey,
          vatRate: tenant?.vatRate !== undefined ? tenant.vatRate : 0.19,
        });
      }

      // 3. Invoke optional external onImportBom callback
      if (onImportBom) {
        await onImportBom(verifiedItems, verifiedData);
      }

      // 4. Update UI feedback banner
      setBannerNotice({
        type: 'success',
        text: `${verifiedItems.length} Position(en) erfolgreich in den Anfragekorb übernommen!`,
      });
      setTimeout(() => setBannerNotice(null), 5000);

      return verifiedData;
    } catch (err) {
      setBannerNotice({
        type: 'error',
        text: err.message || 'Fehler beim Übernehmen der Stückliste in den Anfragekorb.',
      });
      setTimeout(() => setBannerNotice(null), 6000);
      throw err;
    }
  }, [tenant, cartDispatch, onImportBom]);

  const handleSendMessage = async (textToSend) => {
    const query = (textToSend || input).trim();
    if (!query || isLoading) return;

    const userMessage = {
      id: `msg-user-${Date.now()}`,
      role: 'user',
      content: query,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Call Edge Proxy endpoint /api/chat/completions with tenant context
      const response = await fetch('/api/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenant?.id || '',
        },
        body: JSON.stringify(buildCopilotChatRequestBody({
          messages,
          userMessage,
          tenantId: tenant?.id,
          sessionId,
        })),
      });

      if (response.ok) {
        const data = await response.json();

        if (data.status === 'unavailable' || data.ok === false) {
          setBannerNotice({
            type: 'error',
            text: data.error || 'Der Fachassistent ist vorübergehend nicht erreichbar. Bitte wenden Sie sich an Ihren Fachberater.',
          });
          setTimeout(() => setBannerNotice(null), 6000);
        } else if (data.status === 'insufficient_evidence') {
          const assistantText =
            data?.choices?.[0]?.message?.content ||
            data?.content ||
            data?.message ||
            'Zu diesem Thema liegen keine ausreichenden Nachweise in der technischen Dokumentation vor.';

          setMessages((prev) => [
            ...prev,
            {
              id: `msg-asst-${Date.now()}`,
              role: 'assistant',
              content: assistantText,
              bom: null,
              citations: [],
              timestamp: new Date().toISOString(),
            },
          ]);
        } else {
          const assistantText =
            data?.choices?.[0]?.message?.content ||
            data?.content ||
            data?.message ||
            'Antwort erhalten.';
          const bomData = data?.bom || data?.suggestedBom || data?.choices?.[0]?.message?.bom;

          setMessages((prev) => [
            ...prev,
            {
              id: `msg-asst-${Date.now()}`,
              role: 'assistant',
              content: assistantText,
              bom: bomData || null,
              citations: data?.citations || null,
              timestamp: new Date().toISOString(),
            },
          ]);
        }
      } else {
        const errJson = await response.json().catch(() => ({}));
        setBannerNotice({
          type: 'error',
          text: errJson.error || 'Der Fachassistent ist vorübergehend nicht erreichbar. Bitte wenden Sie sich an Ihren Fachberater.',
        });
        setTimeout(() => setBannerNotice(null), 6000);
      }
    } catch {
      setBannerNotice({
        type: 'error',
        text: 'Der Fachassistent ist vorübergehend nicht erreichbar. Bitte überprüfen Sie Ihre Netzwerkverbindung.',
      });
      setTimeout(() => setBannerNotice(null), 6000);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessageRef = useRef(handleSendMessage);
  useEffect(() => {
    sendMessageRef.current = handleSendMessage;
  });

  // Listen for global custom event to open drawer and submit prompt
  useEffect(() => {
    const handleOpenWithQuery = (e) => {
      const query = e.detail?.query || e.detail?.prompt;
      if (onOpenChange) {
        onOpenChange(true);
      }
      if (query && typeof query === 'string' && query.trim()) {
        setTimeout(() => {
          sendMessageRef.current?.(query.trim());
        }, 50);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('asl:open-copilot', handleOpenWithQuery);
      return () => {
        window.removeEventListener('asl:open-copilot', handleOpenWithQuery);
      };
    }
  }, [onOpenChange]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-copilot-drawer="true"
        data-copilot-variant="drawer_flyout"
        className={cn(
          'w-full sm:max-w-lg md:max-w-xl flex flex-col h-full bg-[var(--background,#ffffff)] p-0 shadow-2xl transition-all duration-300 border-l border-[var(--border)]',
          className
        )}
      >
        {/* Drawer Header */}
        <SheetHeader className="flex flex-row items-center justify-between border-b border-[var(--border)] bg-white px-5 py-3.5 space-y-0 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--primary)] text-[var(--primary-foreground,#ffffff)] shadow-xs shrink-0">
              <Bot className="h-5 w-5" />
              <span className="absolute -bottom-0.5 -right-0.5 flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500 border border-white" />
              </span>
            </div>
            
            <div className="min-w-0 text-left">
              <SheetTitle className="text-sm font-bold text-[var(--foreground)] truncate font-display flex items-center gap-1.5">
                <span>{tenantTitle}</span>
              </SheetTitle>
              <div className="flex items-center gap-1.5 text-xs text-[var(--secondary)]">
                <ShieldCheck className="h-3 w-3 text-emerald-600 shrink-0" />
                <span>ALWIS KI &bull; Fachberatung</span>
              </div>
            </div>
          </div>

          {/* Header Action Tools */}
          <div className="flex items-center gap-1 pr-6">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              data-testid="copilot-reset-btn"
              onClick={handleResetChat}
              className="h-8 w-8 text-[var(--secondary)] hover:text-[var(--foreground)] hover:bg-[var(--surface-container-low)] rounded-lg cursor-pointer"
              title="Chat zurücksetzen"
              aria-label="Chat zurücksetzen"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>

        {/* Notification Banner */}
        {bannerNotice && (
          <div
            data-testid="copilot-banner-notice"
            className={cn(
              'border-b px-4 py-2 flex items-center gap-2 text-xs font-semibold animate-in fade-in slide-in-from-top-1',
              bannerNotice.type === 'error'
                ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-emerald-50 border-emerald-200 text-emerald-800'
            )}
          >
            {bannerNotice.type === 'error' ? (
              <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
            ) : (
              <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
            )}
            <span className="flex-1">{bannerNotice.text}</span>
          </div>
        )}

        {/* Active Citation Detail Overlay Bar */}
        {activeCitation && (
          <div className="bg-[var(--surface-container-high,#f1f5f9)] border-b border-[var(--border)] px-4 py-2 flex items-center justify-between gap-2 text-xs">
            <div className="flex items-center gap-1.5 text-[var(--foreground)] font-mono min-w-0">
              <FileText className="h-3.5 w-3.5 text-[var(--primary)] shrink-0" />
              <span className="truncate">Nachweis: <strong>{activeCitation}</strong></span>
            </div>
            <button
              onClick={() => setActiveCitation(null)}
              className="text-xs font-bold text-[var(--secondary)] hover:text-[var(--foreground)] cursor-pointer px-1.5 py-0.5 rounded border border-[var(--border)] bg-white"
            >
              Schließen
            </button>
          </div>
        )}

        {/* Message Transcript Area */}
        <div
          data-testid="copilot-transcript"
          className="flex-1 overflow-y-auto p-4 space-y-4 bg-[var(--background,#ffffff)]"
        >
          {messages.map((msg) => {
            const isAssistant = msg.role === 'assistant';

            return (
              <div
                key={msg.id}
                data-testid="copilot-message"
                data-message-role={msg.role}
                className={cn(
                  'flex gap-3 text-xs leading-relaxed max-w-[92%]',
                  isAssistant ? 'mr-auto items-start' : 'ml-auto flex-row-reverse items-end'
                )}
              >
                {isAssistant && (
                  <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[var(--primary)]/10 text-[var(--primary)] shrink-0 mt-0.5">
                    <Bot className="h-4 w-4" />
                  </div>
                )}

                <div
                  className={cn(
                    'rounded-2xl px-4 py-3 shadow-2xs space-y-2',
                    isAssistant
                      ? 'bg-white border border-[var(--border)] text-[var(--foreground)] rounded-tl-xs'
                      : 'bg-[var(--primary)] text-[var(--primary-foreground,#ffffff)] rounded-br-xs font-medium'
                  )}
                >
                  {isAssistant ? (
                    <CopilotMessageRenderer
                      content={msg.content}
                      onCitationClick={(cit) => setActiveCitation(cit)}
                    />
                  ) : (
                    <div className="whitespace-pre-wrap text-xs leading-relaxed">
                      {msg.content}
                    </div>
                  )}

                  {/* Render BoM Card if attached */}
                  {isAssistant && msg.bom && (
                    <BomCard
                      bom={msg.bom}
                      onImportBom={handleImportBomInternal}
                    />
                  )}
                </div>
              </div>
            );
          })}

          {isLoading && (
            <div className="flex items-center gap-3 mr-auto text-xs text-[var(--secondary)]">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[var(--primary)]/10 text-[var(--primary)] shrink-0">
                <Bot className="h-4 w-4 animate-pulse" />
              </div>
              <div className="flex items-center gap-1.5 rounded-2xl bg-white border border-[var(--border)] px-4 py-2.5 text-xs text-[var(--secondary)] shadow-2xs">
                <Sparkles className="h-3.5 w-3.5 text-[var(--primary)] animate-spin" />
                <span>ALWIS analysiert Normen &amp; Katalogdaten...</span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Suggested Quick Prompt Chips */}
        {messages.length <= 2 && (
          <div className="px-4 py-2 border-t border-[var(--border)] bg-white/70">
            <p className="text-xs font-bold text-[var(--secondary)] mb-1.5 uppercase tracking-wider">
              Vorschläge für Schnellstart:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {DEFAULT_SUGGESTIONS.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  data-testid="copilot-quick-prompt"
                  onClick={() => handleSendMessage(suggestion)}
                  className="rounded-full border border-[var(--border)] bg-[var(--surface-container-low,#f8fafc)] px-2.5 py-1 text-xs font-medium text-[var(--foreground)] hover:border-[var(--primary)] hover:bg-white hover:text-[var(--primary)] transition-all cursor-pointer shadow-2xs text-left"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Bar */}
        <div className="border-t border-[var(--border)] bg-white p-3.5 shrink-0">
          <form
            data-testid="copilot-input-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
            className="flex items-end gap-2"
          >
            <div className="relative flex-1">
              <textarea
                ref={inputRef}
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Frage zu Normen, Datenblättern oder Stücklisten..."
                disabled={isLoading}
                aria-label="Nachricht an ALWIS Fachassistent"
                className="w-full resize-none rounded-xl border border-[var(--border)] bg-[var(--surface-container-low,#f8fafc)] px-3.5 py-2 text-xs text-[var(--foreground)] placeholder:text-muted-foreground focus:bg-white focus:outline-none focus:ring-2 focus:ring-[var(--ring)] focus:border-[var(--primary)] transition-all disabled:opacity-50 max-h-28"
              />
            </div>

            <Button
              type="submit"
              size="sm"
              disabled={isLoading || !input.trim()}
              data-testid="copilot-send-btn"
              className="h-9 px-3 font-bold bg-[var(--primary)] text-[var(--primary-foreground,#ffffff)] hover:opacity-90 transition-opacity shrink-0 cursor-pointer shadow-xs"
              aria-label="Nachricht senden"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>

          <p className="mt-1.5 text-center text-xs text-[var(--secondary)]">
            ALWIS unterstützt unverbindlich bei der B2B-Fachauslegung. Verbindliche Angebote erhalten Sie im Anfragekorb.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
